/**
 * This component represents the Low Confidence Tag.
 * It is used to display a visual indicator for fields with low extraction confidence.
 * The component includes a popover that provides additional details and allows users to mark the data as correct.
 */

import {
  Component,
  ViewChild,
  TemplateRef,
  Input,
  Output,
  EventEmitter,
  ViewContainerRef
} from "@angular/core";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { CommonModule } from "@angular/common";

@Component({
  selector: "app-low-confidence-tag",
  templateUrl: "./low-confidence-tag.component.html",
  styleUrls: ["./low-confidence-tag.component.scss"],
  standalone: true,
  imports: [CommonModule],
})
export class LowConfidenceTagComponent {
  @Input() confidenceScore!: number;
  @Output() markDataCorrect = new EventEmitter<void>();
  @ViewChild("popoverContent", { static: false })
  popoverContent!: TemplateRef<any>;
  overlayRef: OverlayRef | null = null;
  popoverPosition = {
    vertical: "bottom" as "top" | "bottom",
    horizontal: "center" as "left" | "right" | "center",
  };

  constructor(
    private readonly overlay: Overlay,
    private readonly vcr: ViewContainerRef,
  ) {}

  openPopover(event: MouseEvent) {
    if (!this.popoverContent) return;

    const origin = event.target as HTMLElement;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withFlexibleDimensions(false)
      .withGrowAfterOpen(true)
      .withPush(true) // IMPORTANT: allows auto-flip
      .withPositions([
        // ------- Primary: Bottom Start -------
        {
          originX: "start",
          originY: "bottom",
          overlayX: "start",
          overlayY: "top",
          offsetY: 8,
        },

        // ------- Flip Upwards -------
        {
          originX: "start",
          originY: "top",
          overlayX: "start",
          overlayY: "bottom",
          offsetY: -8,
        },

        // ------- Shift Right -------
        {
          originX: "end",
          originY: "bottom",
          overlayX: "end",
          overlayY: "top",
          offsetY: 8,
        },

        // ------- Flip Upwards + Right -------
        {
          originX: "end",
          originY: "top",
          overlayX: "end",
          overlayY: "bottom",
          offsetY: -8,
        },

        // ------- Centered Below -------
        {
          originX: "center",
          originY: "bottom",
          overlayX: "center",
          overlayY: "top",
          offsetY: 8,
        },

        // ------- Centered Above -------
        {
          originX: "center",
          originY: "top",
          overlayX: "center",
          overlayY: "bottom",
          offsetY: -8,
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      panelClass: "popover-panel",
      scrollStrategy: this.overlay.scrollStrategies.reposition(), // ⭐ important
    });

    const portal = new TemplatePortal(this.popoverContent, this.vcr);
    this.overlayRef.attach(portal);
  }

  closePopover() {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  toPercentage(value: number): string {
    return Math.round(value * 100) + "%";
  }

  markCorrect() {
    this.markDataCorrect.emit();
  }
}
